import { useTranslation } from 'react-i18next';
import { heroStats } from '@/mocks/stats';

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative w-full min-h-[820px] flex items-end overflow-hidden bg-foreground-950">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=Abstract%20futuristic%20geometric%20triangular%20crystalline%20structure%20floating%20in%20warm%20amber%20and%20deep%20charcoal%20mist%20atmosphere%2C%20soft%20lime%20green%20accent%20light%20refraction%2C%20cinematic%20editorial%20blockchain%20technology%20photography%2C%20premium%20magazine%20cover%20aesthetic%2C%20minimal%20negative%20space%2C%20subtle%20light%20particles%20drifting%2C%20high%20end%20fintech%20visual%20with%20warm%20cinematic%20grading&width=2000&height=1200&seq=hero-angleforty-2026-01&orientation=landscape')",
        }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-foreground-950/85" />
      {/* Grid overlay */}
      <div className="absolute inset-0 tech-grid-dark opacity-40 animate-grid-drift" />
      {/* Glow */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-primary-500/20 rounded-full blur-[120px] animate-pulse-glow" />
      <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] bg-accent-500/20 rounded-full blur-[120px] animate-pulse-glow" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-10 pt-40 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 mb-8 animate-fade-up"
              style={{ animationDelay: '0.1s' }}
            >
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
              <span className="text-xs font-mono-tech tracking-widest text-background-100 uppercase whitespace-nowrap">
                {t('hero.tag')}
              </span>
            </div>

            <h1 className="font-heading text-background-50 text-5xl md:text-7xl lg:text-[88px] leading-[1.02] font-semibold tracking-tight">
              <span className="block animate-fade-up" style={{ animationDelay: '0.2s' }}>
                {t('hero.title.line1')}
              </span>
              <span className="block animate-fade-up" style={{ animationDelay: '0.35s' }}>
                <span className="text-primary-500 italic font-heading">{t('hero.title.line2')}</span>
              </span>
            </h1>

            <p
              className="mt-8 max-w-2xl text-base md:text-lg text-background-200 leading-relaxed animate-fade-up"
              style={{ animationDelay: '0.5s' }}
            >
              {t('hero.subtitle')}
            </p>

            <div
              className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-up"
              style={{ animationDelay: '0.65s' }}
            >
              <a
                href="#contact"
                className="group inline-flex items-center gap-3 pl-1 pr-6 py-1 bg-foreground-950 hover:bg-foreground-900 rounded-full text-background-50 text-sm font-medium transition-all whitespace-nowrap cursor-pointer border border-background-50/10"
              >
                <span className="w-11 h-11 rounded-full bg-primary-500 flex items-center justify-center text-foreground-950 group-hover:rotate-45 transition-transform">
                  <i className="ri-arrow-right-up-line text-lg w-5 h-5 flex items-center justify-center" />
                </span>
                <span>{t('hero.cta.primary')}</span>
              </a>
              <a
                href="#services"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-background-50 text-sm font-medium border border-background-50/30 hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
              >
                {t('hero.cta.secondary')}
                <i className="ri-arrow-down-line text-base w-4 h-4 flex items-center justify-center" />
              </a>
            </div>
          </div>

          {/* Stats */}
          <div
            className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 pt-10 border-t border-background-50/15 animate-fade-up"
            style={{ animationDelay: '0.8s' }}
          >
            {heroStats.map((s) => (
              <div key={s.labelKey}>
                <div className="font-heading text-3xl md:text-5xl font-semibold text-background-50 tracking-tight">
                  {s.value}
                </div>
                <div className="mt-2 text-xs md:text-sm text-background-200/70 font-mono-tech tracking-wider uppercase">
                  {t(s.labelKey)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Corner marker */}
      <div className="absolute top-28 right-8 hidden lg:flex flex-col items-end gap-2 z-10">
        <div className="w-16 h-16 border border-background-50/25 rounded-full flex items-center justify-center animate-spin-slow">
          <div
            className="w-6 h-6 bg-primary-500"
            style={{ clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }}
          />
        </div>
        <span className="text-[10px] font-mono-tech text-background-100/60 tracking-widest">
          40° · ON-CHAIN
        </span>
      </div>
    </section>
  );
}